import { runtimeConfig } from '../config'
import BaseApi from '@garpix/base-api'
import { Page, PageModelType } from './interfaces/Page'
import { IGame, IGameList } from '@/interfaces/Game'
import { IChampionship, IChampionshipMember } from '@/interfaces/Championship'
import { ICurrentUser, ILogin, IRegister } from '@/interfaces/User'
import { getCookie, removeCookie, setCookie } from '@/utils'
import PaginatedQueryResult from './interfaces/PaginatedQueryResult'
import { Media } from '@/interfaces/Media'
import { News } from '@/interfaces/News'
import { Awards } from '@/interfaces/Awards'
import { IContact } from '@/interfaces/Contacts'
import { ICountry } from '@/interfaces/Country'
import { ILesson } from '@/interfaces/Lessons'

interface Params {
  [n: string]: string | number
}

const API_URL = runtimeConfig.API_URL

const defaultSerializer = (data: any): any => data
const notFoundSerializer = (data: any): any => {
  data.init_state.object = {
    title: '404',
    seo_title: '404'
  }
  return data
}

const PAGES: { [key in PageModelType]: (data: any) => any } = {
  Page: defaultSerializer,
  home_page: defaultSerializer,
  detail_game: defaultSerializer,
  authorization: defaultSerializer,
  games: defaultSerializer,
  news: defaultSerializer,
  news_detail: defaultSerializer,
  profile: defaultSerializer,
  cabinet_championships: defaultSerializer,
  cabinet_contests: defaultSerializer,
  cabinet_certificates: defaultSerializer,
  forgot_your_password: defaultSerializer,
  restore_password_set_password: defaultSerializer,
  leaders_contacts: defaultSerializer,
  media_detail: defaultSerializer,
  championships: defaultSerializer,
  championship_application: defaultSerializer,
  championship_detail: defaultSerializer,
  tournaments: defaultSerializer,
  tournaments_detail: defaultSerializer,
  live_like_a_host: defaultSerializer,
  education: defaultSerializer,
  best_in_profession: defaultSerializer,
  recommend_to_know: defaultSerializer,
  quiz: defaultSerializer,
  institution_detail: defaultSerializer,
  contest_detail: defaultSerializer,
  add_work: defaultSerializer,
  urban_club: defaultSerializer,
  lecture: defaultSerializer,
  success_story: defaultSerializer,
  lesson: defaultSerializer,
  privacy_policy: defaultSerializer,
  personal_data_policy: defaultSerializer,
  profession_competition: defaultSerializer,
  what_read: defaultSerializer,
  500: defaultSerializer,
  404: notFoundSerializer
}

class Api extends BaseApi {
  AUTH_TOKEN_KEY: string

  constructor (MAIN_URL) {
    super(MAIN_URL)
    this.AUTH_TOKEN_KEY = 'jeka_auth_token'
  }

  getLanguage = (): string => 'ru'

  axiosOverride = (axios): any => {
    axios.defaults.headers.common['Accept-Language'] = this.getLanguage()
    const token = this.getAuthToken()
    if (token !== null) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`
    }
    return axios
  }

  getPage = async (
    pathname: string,
    queryParams = {},
    axiosParams = {}
  ): Promise<{ pageType: string, page: any, statusCode: number }> => {
    try {
      const path = pathname.charAt(0) === '/' ? pathname : `/${pathname}`
      const res: { data: Page } = await this.get(
        `/page${path}`,
        queryParams,
        axiosParams
      )
      const page = res.data
      const serializer = PAGES[page.page_model]
      return {
        pageType: page.page_model,
        page: serializer(page),
        statusCode: 200
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        const serializer = PAGES['404']
        return {
          pageType: '404',
          statusCode: 404,
          page: serializer(error.response.data)
        }
      }
      const serializer = PAGES['500']
      return {
        pageType: '500',
        page: serializer({}),
        statusCode: 500
      }
    }
  }

  getAuthToken = (): string | null => {
    return getCookie(this.AUTH_TOKEN_KEY)
  }

  setAuthToken = (token): boolean => {
    setCookie(this.AUTH_TOKEN_KEY, token)
    return true
  }

  deleteAuthToken = (): void => {
    removeCookie(this.AUTH_TOKEN_KEY)
  }

  login = async (params): Promise<any> => {
    const result: { data: ILogin } = await this.post('/users/login/', params)
    this.setAuthToken(result.data.token)
    return result.data
  }

  setPassword = async (params): Promise<any> => {
    const result: { data: ILogin } = await this.post(
      '/users/set_password/',
      params
    )
    return result.data
  }

  register = async (params): Promise<any> => {
    const result: { data: IRegister } = await this.post(
      '/users/register/',
      params
    )
    return result.data
  }

  getCountries = async (): Promise<any> => {
    const result: { data: { results: ICountry } } = await this.get(
      '/users/country/'
    )
    return result.data
  }

  getCurrentUser = async (): Promise<ICurrentUser> => {
    const result: { data: ICurrentUser } = await this.get('/users/current/')
    return result.data
  }

  updateUser = async (params): Promise<any> => {
    const result: { data: any } = await this.post('/users/update/', params)
    return result.data
  }

  setAvatarUser = async (params: { avatar: any }): Promise<any> => {
    const fd = new FormData()
    Object.keys(params).forEach(key => {
      const value = params[key]
      fd.set(key, value)
    })
    const result: { data: any } = await this.post('/users/avatar/', fd)
    return result.data
  }

  logout = async (): Promise<any> => {
    await this.post('/users/logout/', {})
    this.deleteAuthToken()
  }

  sendFeedback = async (params): Promise<any> => {
    const res: { data: any } = await this.post('/feedback/', params)
    return res.data
  }

  getUserChampionships = async (
    params?
  ): Promise<PaginatedQueryResult<IChampionship>> => {
    const result: {
      data: PaginatedQueryResult<IChampionship>
    } = await this.get('/users/championships/', { status: 'open', ...params })
    return result.data
  }

  restorePassword = async (params): Promise<any> => {
    const result: { data: any } = await this.post(
      '/users/restore_password/',
      params
    )

    return result.data
  }

  restorePasswordSetPassword = async (params): Promise<any> => {
    const result: { data: any } = await this.post(
      '/users/restore_password_set_password/',
      params
    )

    return result.data
  }

  getGames = async (params): Promise<PaginatedQueryResult<IGameList>> => {
    const res: { data: PaginatedQueryResult<IGameList> } = await this.get(
      '/games/',
      params
    )
    return res.data
  }

  getGame = async (id: string | number): Promise<IGame> => {
    const res: { data: IGame } = await this.get(`/games/${id}/`)
    return res.data
  }

  getLastGame = async (): Promise<IGame> => {
    const res: { data: IGame } = await this.get('/games/main/')
    return res.data
  }

  getMediaList = async (params): Promise<PaginatedQueryResult<Media>> => {
    const res: { data: PaginatedQueryResult<Media> } = await this.get(
      '/media/',
      params
    )
    return res.data
  }

  getMedia = async (params: {
    id: string | number
  }): Promise<PaginatedQueryResult<Media>> => {
    const res: { data: PaginatedQueryResult<Media> } = await this.get(
      `/media/${params.id}/`,
      params
    )
    return res.data
  }

  getNewsList = async (params): Promise<PaginatedQueryResult<News>> => {
    const res: { data: PaginatedQueryResult<News> } = await this.get(
      '/news/',
      params
    )
    return res.data
  }

  getNews = async (params: { id: string | number }): Promise<News> => {
    const res: { data: News } = await this.get(`/news/${params.id}/`, params)
    return res.data
  }

  getMainNews = async (): Promise<any> => {
    const res: { data: News } = await this.get('/news/main/')
    return res.data
  }

  getChampionships = async (params): Promise<any> => {
    const res: { data: IChampionship } = await this.get('/championships/', {
      ...params,
      tournament: '0'
    })
    return res.data
  }

  getChampionship = async (params: {
    id: string | number
  }): Promise<IChampionship> => {
    const res: { data: IChampionship } = await this.get(
      `/championships/${params.id}/`,
      params
    )
    return res.data
  }

  getChampionshipMembers = async ({
    id,
    ...params
  }: {
    id: string | number
  }): Promise<PaginatedQueryResult<IChampionshipMember>> => {
    const res: {
      data: PaginatedQueryResult<IChampionshipMember>
    } = await this.get(`/championships/${id}/members/`, params)
    return res.data
  }

  getChampionshipsRegistered = async ({
    id,
    ...params
  }: Params): Promise<any> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      `/championships/${id}/registered/`,
      params
    )
    return res.data
  }

  getChampionshipResultsStage = async ({
    id,
    stage,
    ...params
  }: Params): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      `/championships/${id}/results/${stage}/`,
      params
    )
    return res.data
  }

  getTournamentsMembers = async ({
    stage,
    ...params
  }: Params): Promise<any> => {
    if (String(stage) === '1') {
      return await this.getChampionshipsRegistered({ stage, ...params })
    } else {
      const res = await this.getChampionshipResultsStage({ stage, ...params })
      res.results = res.results.map(item => {
        return {
          ...item,
          user: {
            personal_photo: item.avatar,
            id: item.user_id,
            personal_state: item.region,
            personal_city: item.city,
            name: item.name
          }
        }
      })
      return res
    }
  }

  createChampionship = async (params): Promise<any> => {
    const res: { data: any } = await this.post(
      '/championships/create/',
      params
    )
    return res.data
  }

  getTournaments = async (
    params
  ): Promise<PaginatedQueryResult<IChampionship>> => {
    const res: {
      data: PaginatedQueryResult<IChampionship>
    } = await this.get('/championships/', { ...params, tournament: '1' })
    return res.data
  }

  closeChampionship = async ({ id }: { id: string | number }): Promise<any> => {
    const res: { data: any } = await this.post(
      `/championships/${id}/close/`,
      {}
    )
    return res.data
  }

  tournamentRegistration = async (params): Promise<any> => {
    const res: { data: any } = await this.post(
      '/championships/tournament_registration/',
      params
    )
    return res.data
  }

  getAwardsList = async (params): Promise<PaginatedQueryResult<Awards>> => {
    const res: { data: PaginatedQueryResult<Awards> } = await this.get(
      '/awards/',
      params
    )
    return res.data
  }

  makeCertificate = async (params): Promise<string> => {
    const res: any = await this.post('/certificate/', params, {
      responseType: 'arraybuffer'
    })
    const base64 = Buffer.from(res.data, 'binary').toString('base64')
    return `data:image/png;base64,${base64}`
  }

  getContacts = async (params): Promise<PaginatedQueryResult<IContact>> => {
    const res: { data: PaginatedQueryResult<IContact> } = await this.get(
      '/contacts/',
      params
    )
    return res.data
  }

  getLifeHacks = async (params?): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/life_hacks/',
      params
    )
    return res.data
  }

  getLifeHack = async ({ id }: { id: number | string }): Promise<any> => {
    const res: { data: any } = await this.get(`/life_hacks/${id}/`)
    return res.data
  }

  getQuizList = async (params?): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/quiz/',
      params
    )
    return res.data
  }

  getQuiz = async ({ id }: { id: number | string }): Promise<any> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      `/quiz/${id}/`
    )
    return res.data
  }

  getInstitutionDetail = async ({
    id
  }: {
    id: number | string
  }): Promise<any> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      `/education/${id}/`
    )
    return res.data
  }

  getContests = async (params?): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/contests/',
      params
    )
    return res.data
  }

  getContest = async ({ id }: { id: number | string }): Promise<any> => {
    const res: { data: any } = await this.get(`/contests/${id}/`)
    return res.data
  }

  getContestWorks = async (params: {
    id: number | string
  }): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      `/contests/${params.id}/works/`,
      params
    )
    return res.data
  }

  setContestWork = async (
    { id }: { id: number | string },
    params: object
  ): Promise<any> => {
    const fd = new FormData()
    Object.keys(params).forEach(key => {
      const value = params[key]
      fd.set(key, value)
    })
    const result: { data: any } = await this.post(
      `/contests/${id}/add_work/`,
      fd
    )
    return result.data
  }

  getContestsUser = async (params?): Promise<any> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/contests/user/',
      params
    )
    return res.data
  }

  getSuccessStoryList = async (params?): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/success_story/',
      params
    )
    return res.data
  }

  getSuccessStory = async ({ id }: { id: number | string }): Promise<any> => {
    const res: { data: any } = await this.get(`/success_story/${id}/`)
    return res.data
  }

  getProfessionsVideo = async (): Promise<any> => {
    const res: { data: any } = await this.get('/professions_video/')
    return res.data
  }

  getProfessionCompetition = async (params?): Promise<PaginatedQueryResult<any>> => {
    const res: { data: any } = await this.get('/profession_competition/', params)
    return res.data
  }

  getProfessionCompetitionDetail = async ({
    id
  }: {
    id: number | string
  }): Promise<any> => {
    const res: { data: any } = await this.get(`/profession_competition/${id}/`)
    return res.data
  }

  getEducationList = async (params?): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/education/',
      params
    )
    return res.data
  }

  getEducationSpecials = async (
    params?
  ): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/education/specials/',
      params
    )
    return res.data
  }

  getEducationRegions = async (params?): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/education/regions/',
      params
    )
    return res.data
  }

  getLessons = async (params?): Promise<PaginatedQueryResult<ILesson>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/lessons/',
      params
    )
    return res.data
  }

  getLesson = async ({ id }: { id: number | string }): Promise<any> => {
    const res: { data: any } = await this.get(`/lessons/${id}/`)
    return res.data
  }

  getVideoReviews = async (params?): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/video_reviews/',
      params
    )
    return res.data
  }

  getWhatRead = async (params?): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/what_read/',
      params
    )
    return res.data
  }

  getWhatReadDetail = async ({ id }: { id: number | string }): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(`/what_read/${id}/`)
    return res.data
  }

  getWhatReadSections = async (): Promise<any> => {
    const res: { data: { results: any[] } } = await this.get(
      '/what_read/sections/'
    )
    return res.data
  }

  getMaterialsClubUrbanists = async (
    params?
  ): Promise<PaginatedQueryResult<any>> => {
    const res: { data: PaginatedQueryResult<any> } = await this.get(
      '/materials_club_urbanists/',
      params
    )
    return res.data
  }

  getMapInfo = async (params?): Promise<any> => {
    const res: { data: any } = await this.get('/info_map/', params)
    return res.data
  }

  getTutorialText = async (): Promise<any> => {
    const res: { data: any } = await this.get('/tutorial/', {})
    return res.data
  }
}

const api = new Api(`${String(API_URL)}`)

export { api }
